import { BOARD, GROUPS, LEVEL_NAMES } from '../game/board'
import { assetSaleNet, mortgageAmount, propertySaleNet } from '../game/finance'
import { useGame } from '../store/gameStore'
import type { GameState, Player } from '../game/types'
import { money, signed } from '../util/format'

/**
 * แผงระดมเงินตอนเงินสดติดลบ
 * ต้องอยู่ "ในหน้าต่าง" ไม่ใช่ให้ไปกดแผงข้าง เพราะหน้าต่างบังทั้งจอไว้อยู่แล้ว
 */
export function RaiseCash({ g, p }: { g: GameState; p: Player }) {
  const { dumpAsset, mortgage, dumpProperty } = useGame()
  const need = -p.cash

  const owned = g.tiles
    .map((ts, id) => ({ ts, id }))
    .filter((x) => x.ts.ownerId === p.id)

  const rows: {
    key: string
    emoji: string
    name: string
    detail: string
    amount: number
    color?: string
    action: () => void
    label: string
  }[] = []

  for (const a of p.assets) {
    rows.push({
      key: `a-${a.uid}`,
      emoji: a.emoji,
      name: a.name,
      detail: `มูลค่า ${money(a.value)} · กระแสเงินสด ${signed(a.cashflow)}/ด.`,
      amount: assetSaleNet(p, a.uid),
      action: () => dumpAsset(a.uid),
      label: 'ขาย',
    })
  }

  for (const { id, ts } of owned) {
    const tile = BOARD[id]
    if (!ts.mortgaged) {
      rows.push({
        key: `m-${id}`,
        emoji: '🏚️',
        name: `จำนอง ${tile.name}`,
        detail: 'ยังถือโฉนดไว้ แต่หยุดเก็บค่าเช่าและกระแสเงินสด',
        amount: mortgageAmount(id),
        color: tile.group ? GROUPS[tile.group].color : undefined,
        action: () => mortgage(id),
        label: 'จำนอง',
      })
    }
    rows.push({
      key: `s-${id}`,
      emoji: tile.emoji,
      name: `ขาย ${tile.name}`,
      detail: `${tile.kind === 'property' ? LEVEL_NAMES[ts.level] : 'ปล่อยคืนธนาคาร'}${ts.mortgaged ? ' · จำนองไว้แล้ว' : ''}`,
      amount: propertySaleNet(g, p, id),
      color: tile.group ? GROUPS[tile.group].color : undefined,
      action: () => dumpProperty(id),
      label: 'ขาย',
    })
  }

  // เรียงให้ตัวที่ปลดหนี้ได้เยอะสุดขึ้นก่อน จะได้เลือกง่าย
  rows.sort((a, b) => b.amount - a.amount)

  return (
    <div className="raise">
      <div className={`raise-head${need <= 0 ? ' ok' : ''}`}>
        <span>{need > 0 ? 'ยังขาดอีก' : '✅ เงินสดพอแล้ว เหลือ'}</span>
        <strong className={`num ${need > 0 ? 'bad-text' : 'good-text'}`}>
          {money(Math.abs(need))}
        </strong>
      </div>

      {rows.length === 0 ? (
        <p className="faint tiny center">ไม่เหลืออะไรให้ขายแล้ว — กดปุ่มด้านล่างเพื่อจบเกมของคุณ</p>
      ) : (
        <div className="raise-list">
          {rows.map((r) => (
            <div key={r.key} className="raise-row">
              {r.color && <span className="raise-chip" style={{ background: r.color }} />}
              <span className="raise-emoji">{r.emoji}</span>
              <span className="raise-main">
                <b>{r.name}</b>
                <em className="faint tiny">{r.detail}</em>
              </span>
              <span className={`raise-amt num ${r.amount >= 0 ? 'good-text' : 'bad-text'}`}>
                {signed(r.amount)}
              </span>
              <button
                className={`btn btn-sm ${r.amount >= need ? 'btn-primary' : ''}`}
                onClick={r.action}
                title={r.amount < 0 ? 'ชิ้นนี้หนี้ท่วมมูลค่า ขายแล้วเงินยิ่งติดลบ' : ''}
              >
                {r.label}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
